import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Calendar, Shield, RefreshCw, History, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { ApiKey, useApiKeyHistory } from '../../api/hooks/apikeys/useApiKeyQuery';
import { UpdateApiKeyInput, useUpdateApiKey, useRegenerateApiKey } from '../../api/hooks/apikeys/useApiKeyMutation';

interface ApiKeyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey | null;
  initialEditMode?: boolean;
}

export const ApiKeyDetailsModal: React.FC<ApiKeyDetailsModalProps> = ({
  isOpen,
  onClose,
  apiKey,
  initialEditMode = false
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [regeneratedKey, setRegeneratedKey] = useState<string | null>(null);
  const [showRegeneratedKey, setShowRegeneratedKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const updateApiKey = useUpdateApiKey();
  const regenerateApiKey = useRegenerateApiKey();
  const { data: historyData } = useApiKeyHistory(apiKey?.id || null);

  const { register, handleSubmit, reset } = useForm<UpdateApiKeyInput>();

  // Reset regenerated key when modal closes or apiKey changes
  useEffect(() => {
    if (!isOpen) {
      setRegeneratedKey(null);
      setShowRegeneratedKey(false);
      setCopied(false);
    }
  }, [isOpen, apiKey?.id]);

  // Update form when apiKey changes or when entering edit mode
  useEffect(() => {
    if (apiKey && isOpen) {
      reset({
        name: apiKey.name,
        expiresAt: apiKey.expiresAt ? apiKey.expiresAt.slice(0, 16) : '',
        rateLimit: apiKey.rateLimit,
        ipWhitelist: apiKey.ipWhitelist?.join(', ') as any || '',
        isActive: apiKey.isActive,
      });
    }
  }, [apiKey, reset, isOpen]);

  // Set edit mode when prop changes
  useEffect(() => {
    setIsEditing(initialEditMode);
  }, [initialEditMode]);

  const onSubmit = async (data: UpdateApiKeyInput) => {
    if (!apiKey) return;

    try {
      // Convert datetime-local to ISO 8601 format
      if (data.expiresAt && data.expiresAt.trim() !== '') {
        data.expiresAt = new Date(data.expiresAt).toISOString();
      } else {
        delete data.expiresAt;
      }
      
      // Convert comma-separated IPs to array if provided
      if (typeof data.ipWhitelist === 'string') {
        const ipString = data.ipWhitelist as any;
        if (ipString.trim() === '') {
          // Empty string should be undefined or empty array
          delete data.ipWhitelist;
        } else {
          data.ipWhitelist = ipString
            .split(',')
            .map((ip: string) => ip.trim())
            .filter((ip: string) => ip.length > 0);
        }
      }

      console.log('[API Key Update] Submitting data:', data);
      console.log('[API Key Update] Rate limit value:', data.rateLimit, 'Type:', typeof data.rateLimit);
      await updateApiKey.mutateAsync({ keyId: apiKey.id, data });
      setIsEditing(false);
      reset(data);
    } catch (error) {
      console.error('Failed to update API key:', error);
    }
  };

  const handleRegenerate = async () => {
    if (!apiKey) return;
    if (!confirm(`Are you sure you want to regenerate this API key? The current key will stop working immediately.`)) {
      return;
    }

    try {
      const result = await regenerateApiKey.mutateAsync(apiKey.id);
      setRegeneratedKey(result.key);
      setShowRegeneratedKey(true);
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
    }
  };

  const handleCopyKey = async () => {
    if (regeneratedKey) {
      await navigator.clipboard.writeText(regeneratedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen || !apiKey) return null;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-bold">API Key Details</h2>
          {!isEditing && (
            <div className="flex space-x-2">
              {apiKey.isActive && (
                <button
                  onClick={handleRegenerate}
                  disabled={regenerateApiKey.isPending}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 flex items-center"
                >
                  <RefreshCw size={16} className={`mr-2 ${regenerateApiKey.isPending ? 'animate-spin' : ''}`} />
                  {regenerateApiKey.isPending ? 'Regenerating...' : 'Regenerate'}
                </button>
              )}
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                {...register('name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                {...register('isActive', {
                  setValueAs: v => v === 'true'
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiration Date
              </label>
              <input
                type="datetime-local"
                {...register('expiresAt')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rate Limit (requests per hour)
              </label>
              <input
                type="number"
                {...register('rateLimit', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                IP Whitelist
              </label>
              <input
                type="text"
                {...register('ipWhitelist' as any)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="192.168.1.1, 10.0.0.0/24"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  reset();
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateApiKey.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateApiKey.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Regenerated Key Display */}
            {regeneratedKey && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2 text-green-800">New API Key Generated!</h3>
                <p className="text-sm text-green-700 mb-3">
                  Save this key securely. It will not be shown again.
                </p>
                <div className="flex items-center space-x-2 bg-white rounded-lg p-3 border border-green-200">
                  <code className="flex-1 text-sm font-mono break-all">
                    {showRegeneratedKey ? regeneratedKey : '•'.repeat(40)}
                  </code>
                  <button
                    onClick={() => setShowRegeneratedKey(!showRegeneratedKey)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                    title={showRegeneratedKey ? 'Hide key' : 'Show key'}
                  >
                    {showRegeneratedKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={handleCopyKey}
                    className="p-2 text-gray-500 hover:text-gray-700"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Key Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Key Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium">{apiKey.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Key Prefix</p>
                  <p className="font-mono text-sm">{apiKey.keyPrefix}...</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    apiKey.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {apiKey.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Requests</p>
                  <p className="font-medium">{apiKey.totalRequests.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Dates
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="text-sm">{formatDate(apiKey.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Used</p>
                  <p className="text-sm">
                    {apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : 'Never'}
                  </p>
                </div>
                {apiKey.expiresAt && (
                  <div>
                    <p className="text-sm text-gray-600">Expires</p>
                    <p className="text-sm">{formatDate(apiKey.expiresAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Security Settings
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Rate Limit</p>
                  <p className="text-sm">
                    {apiKey.rateLimit ? `${apiKey.rateLimit} requests/hour` : 'Default (1000/hour)'}
                  </p>
                </div>
                {apiKey.ipWhitelist && apiKey.ipWhitelist.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">IP Whitelist</p>
                    <div className="space-y-1">
                      {apiKey.ipWhitelist.map((ip, index) => (
                        <p key={index} className="text-sm font-mono bg-white px-2 py-1 rounded">
                          {ip}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Key History */}
            {historyData?.history && historyData.history.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center">
                  <History className="w-4 h-4 mr-2" />
                  Key History
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Previous key versions (for comparison with usage logs)
                </p>
                <div className="space-y-2">
                  {historyData.history.map((entry) => (
                    <div key={entry.historyId} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div>
                        <code className="text-sm font-mono text-gray-600">
                          {entry.keyPrefix}...
                        </code>
                        <p className="text-xs text-gray-400">
                          Created: {formatDate(entry.originalCreatedAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          Regenerated: {formatDate(entry.regeneratedAt)}
                        </p>
                        {entry.regeneratedBy && (
                          <p className="text-xs text-gray-400">
                            by {entry.regeneratedBy}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};